import { create } from 'zustand';

/**
 * Last-reviewer confirmation (SSOT)
 *
 * The backend answers 409 `release.last_reviewer` when a mutation would leave
 * a workflow-active brand without a reviewer. The axios response interceptor
 * (services/api.ts) calls `requestLastReviewerConfirm()`, which opens ONE
 * global modal (LastReviewerConfirmHost) and resolves with the user's choice.
 */

export const LAST_REVIEWER_ERROR_CODE = 'release.last_reviewer';

export interface LastReviewerBrand {
  teamId: number;
  name: string;
}

interface PendingConfirm {
  brands: LastReviewerBrand[];
  resolve: (confirmed: boolean) => void;
}

interface LastReviewerConfirmStore {
  pending: PendingConfirm | null;
  open: (brands: LastReviewerBrand[]) => Promise<boolean>;
  settle: (confirmed: boolean) => void;
}

/** Serialises concurrent requests so only one dialog is shown at a time. */
let queue: Promise<unknown> = Promise.resolve();

export const useLastReviewerConfirmStore = create<LastReviewerConfirmStore>((set, get) => ({
  pending: null,
  open: (brands) => {
    const run = (): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        set({ pending: { brands, resolve } });
      });
    const next = queue.then(run, run);
    queue = next;
    return next;
  },
  settle: (confirmed) => {
    const { pending } = get();
    if (!pending) return;
    set({ pending: null });
    pending.resolve(confirmed);
  },
}));

/** Opens the confirmation dialog; resolves true when the user chose "Remove anyway". */
export const requestLastReviewerConfirm = (brands: LastReviewerBrand[]): Promise<boolean> =>
  useLastReviewerConfirmStore.getState().open(brands);

/**
 * Rejection used when the user cancels. Callers that catch request errors and toast
 * MUST check `isLastReviewerCancel(err)` first and return quietly (scoped per error,
 * never a global mute).
 */
export class LastReviewerCancelledError extends Error {
  readonly isLastReviewerCancel = true;
  constructor() {
    super('Cancelled: last reviewer removal not confirmed');
    this.name = 'LastReviewerCancelledError';
  }
}

export const isLastReviewerCancel = (error: unknown): boolean =>
  error instanceof LastReviewerCancelledError;
